import { z } from "zod";

export const TOPIC_SOURCE_LABELS = {
  audience: "受眾問什麼",
  practice: "你在做什麼",
  judgment: "你提出的判斷",
  past_content: "過去的內容",
  platform: "平台與外部訊號",
} as const;
export const TOPIC_STYLE_LABELS = {
  explanation: "解釋法",
  method: "方法論",
  comparison: "比較法",
  case: "案例法",
  mistake: "錯誤法",
} as const;
export const topicSourceSchema = z.enum(["audience", "practice", "judgment", "past_content", "platform"]);
export const topicStyleSchema = z.enum(["explanation", "method", "comparison", "case", "mistake"]);
export type TopicSource = z.infer<typeof topicSourceSchema>;
export type TopicStyle = z.infer<typeof topicStyleSchema>;
export const TOPIC_SOURCES = Object.keys(TOPIC_SOURCE_LABELS) as TopicSource[];
export const TOPIC_STYLES = Object.keys(TOPIC_STYLE_LABELS) as TopicStyle[];
export const TOPIC_PLANNING_VERSION = "competitor-5x5-v1";
export const WRITING_TOPIC_STATUSES = ["suggested", "brief_confirmed", "drafting"];

export const topicEvidenceSchema = z.object({
  id: z.string().min(1),
  keywordId: z.number().int().positive(),
  sources: z.array(topicSourceSchema).min(1),
  kind: z.enum(["search_result", "search_question", "monitoring_question", "ai_answer", "official_product", "knowledge_card", "past_article"]),
  title: z.string().min(1),
  text: z.string().min(1),
  url: z.string().nullable(),
  observedAt: z.string().nullable(),
  provenance: z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
});
export type TopicEvidence = z.infer<typeof topicEvidenceSchema>;
export const occupiedSignalSchema = z.object({
  provider: z.enum(["aio", "gemini", "chatgpt"]),
  checkedAt: z.string().min(1),
  runId: z.number().int().positive(),
  observationId: z.number().int().positive(),
  query: z.string().min(1),
  competitors: z.array(z.string().min(1)).min(1),
  answerEvidenceId: z.string().min(1),
});
export type OccupiedTopicSignal = z.infer<typeof occupiedSignalSchema>;
export const evidenceReferenceSchema = z.object({
  field: z.enum(["title", "person", "scene", "coreProblem", "decisionTask", "faq"]),
  evidenceId: z.string().min(1),
  quote: z.string().min(4),
});
export type TopicEvidenceReference = z.infer<typeof evidenceReferenceSchema>;
export const topicPlanningSchema = z.object({
  version: z.literal(TOPIC_PLANNING_VERSION),
  batchId: z.string().min(1),
  keywordId: z.number().int().positive(),
  keyword: z.string().min(1),
  source: topicSourceSchema,
  style: topicStyleSchema,
  person: z.string().min(1),
  scene: z.string().min(1),
  coreProblem: z.string().min(1),
  decisionTask: z.string().min(1),
  occupiedSignals: z.array(occupiedSignalSchema).min(1),
  evidence: z.array(topicEvidenceSchema).min(1),
  references: z.array(evidenceReferenceSchema).min(5),
  matrix: z.array(z.object({
    source: topicSourceSchema,
    style: topicStyleSchema,
    evidenceIds: z.array(z.string()),
    status: z.enum(["selected", "available", "missing_source"]),
    reason: z.string().min(1),
  })).length(25),
  duplicateCheck: z.object({
    comparedTopicIds: z.array(z.number().int().positive()),
    comparedProposalIds: z.array(z.string()),
    reason: z.string().min(1),
  }),
  sourceIssues: z.array(z.string()),
  plannedAt: z.string().min(1),
});
export type ArticleTopicPlanning = z.infer<typeof topicPlanningSchema>;

export function parseArticleTopicPlanning(rawBrief: string | null | undefined): ArticleTopicPlanning | null {
  if (!rawBrief) return null;
  const brief = JSON.parse(rawBrief);
  if (!brief?.planning) return null;
  const result = topicPlanningSchema.safeParse(brief.planning);
  if (!result.success) throw new Error("題目出題依據格式錯誤");
  return result.data;
}

export type PlanningKeyword = {
  id: number;
  keyword: string;
  aiSignals?: Partial<Record<"aio" | "gemini" | "chatgpt", {
    status?: string;
    marketState?: string | null;
  }>>;
};

export function competingTopicProviders(keyword: PlanningKeyword) {
  return (["aio", "gemini", "chatgpt"] as const).filter(provider =>
    keyword.aiSignals?.[provider]?.status === "analyzed"
    && keyword.aiSignals?.[provider]?.marketState === "competitor_occupied"
  );
}

export function eligibleTopicKeywords<T extends PlanningKeyword>(keywords: T[]): T[] {
  return keywords
    .map((keyword, index) => ({ keyword, index, count: competingTopicProviders(keyword).length }))
    .filter(row => row.count > 0 && row.keyword.keyword.trim())
    .sort((a, b) => b.count - a.count || a.index - b.index || a.keyword.id - b.keyword.id)
    .map(row => row.keyword);
}

export function buildTopicMatrix(evidence: TopicEvidence[], selected?: { source: TopicSource; style: TopicStyle }) {
  return TOPIC_SOURCES.flatMap(source => TOPIC_STYLES.map(style => {
    const evidenceIds = evidence.filter(item => item.sources.includes(source)).map(item => item.id);
    const chosen = source === selected?.source && style === selected?.style;
    return {
      source, style, evidenceIds,
      status: chosen ? "selected" as const : evidenceIds.length ? "available" as const : "missing_source" as const,
      reason: chosen ? "本題選用的來源與寫法" : evidenceIds.length ? "已有來源，本輪未選此切角" : `尚無可追溯的「${TOPIC_SOURCE_LABELS[source]}」資料`,
    };
  }));
}
